import { Children, type PropsWithChildren } from 'react';
import {
  animated,
  easings,
  useSprings,
  type SpringValues,
} from '@react-spring/web';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';

interface GridProps extends BoxT {
  numberOfColumns: number;
  shouldAnimate?: boolean;
}

export const Grid = ({
  numberOfColumns,
  shouldAnimate = false,
  children,
  ...rest
}: PropsWithChildren<GridProps>) => {
  const items = Children.toArray(children);
  const ANIMATION_DELAY = 350;
  const CHILD_ANIMATION_DELAY_FACTOR = 80;
  const SECOND_ROW_DELAY = 200;

  const [springs] = useSprings(
    items.length,
    (i) => ({
      from: { opacity: 0, x: i % numberOfColumns === 0 ? -60 : -100 },
      opacity: 1,
      x: 0,
      delay:
        ANIMATION_DELAY +
        i * CHILD_ANIMATION_DELAY_FACTOR +
        Math.floor(i / numberOfColumns) * SECOND_ROW_DELAY,
      config: {
        duration: 500,
        easing: easings.easeOutBack,
      },
    }),
    [],
  );

  return (
    <Box
      width="100%"
      display="grid"
      gridTemplateColumns={`repeat(${numberOfColumns}, 1fr)`}
      gap={4}
      {...rest}
    >
      {springs.map((style, index) => {
        const child = items[index];
        return (
          <Box
            key={index}
            as={animated.div}
            style={shouldAnimate ? (style as SpringValues) : {}}
            width="100%"
          >
            {/* eslint-disable-next-line menti-react-jsx/no-possibly-undefined-in-jsx */}
            {child}
          </Box>
        );
      })}
    </Box>
  );
};
